/**
 * Hands-free audio transcription module (plain TypeScript, no React).
 *
 * - Exports a class you can instantiate.
 * - updateInputs(inputs) is a stub but demonstrates how you'd trigger start/stop.
 * - render(canvas) draws simple status + last transcript.
 *
 * Recording stops automatically after "silenceMs" of silence using WebAudio RMS detection.
 * Then audio is POSTed as multipart/form-data to your transcription endpoint.
 * Test the dev version here: https://metapage.io/m/e147047f42454b548730f737ba1ef53a
 */

import {
  ClenchData,
  ConnectOptions,
  HitBox,
  MediapipePoseResults,
  MicrophoneControlOptions,
  Rect,
} from "/@/shared/motion-menu-system/MenuTypes";

import { getFaceHitBoxFromPose } from "./common.ts";

export type HandsFreeTranscriberOptions = {
  endpoint?: string; // default "/api/transcribe"
  silenceMs?: number; // stop after this much silence
  minRecordingMs?: number; // minimum record time to avoid early cut-off
  sampleIntervalMs?: number; // VAD polling interval
  initialThreshold?: number; // 0 = auto-calibrate
  extraFormFields?: Record<string, string>; // e.g. { language: "en" }
  debugVad?: boolean;
  /**
   * When true (default), recording auto-stops after detecting silence.
   * When false, you must manually call stop() to end recording and trigger transcription.
   */
  handsFree?: boolean;
  /**
   * How long (ms) the clench must consistently intersect the face hitbox before starting recording.
   * Helps avoid accidental triggers from jittery detection. Default: 150ms
   */
  clenchOnDelayMs?: number;
  /**
   * How long (ms) the clench must consistently NOT intersect the face hitbox before stopping recording.
   * Helps avoid premature stops from jittery detection. Default: 200ms
   */
  clenchOffDelayMs?: number;
};

export type UpdateInputs = Record<string, unknown> & {
  // stub examples (you can change these later)
  shouldListen?: boolean; // if true -> ensure recording, if false -> stop
  // or a "trigger" style:
  startListening?: boolean;
  stopListening?: boolean;
};

type Phase =
  | "idle"
  | "recording"
  | "uploading"
  | "transcribing"
  | "done"
  | "error";

function pickMimeType(): string {
  const candidates = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/ogg;codecs=opus",
    "audio/ogg",
  ];
  return candidates.find((t) => MediaRecorder.isTypeSupported(t)) || "";
}

function clamp(n: number, a: number, b: number) {
  return Math.max(a, Math.min(b, n));
}

async function sleep(ms: number) {
  return new Promise((r) => setTimeout(r, ms));
}

const EMPTY_ARRAY: HitBox[] = [];
const EMPTY_OBJECT: ClenchData = {};

const isClenchBoxIntersectingFace = (
  clenchBox?: HitBox,
  face?: Rect | null,
) => {
  if (!clenchBox || !face) {
    return false;
  }

  return clenchBox.x < face.x + face.width &&
    clenchBox.x + clenchBox.width > face.x &&
    clenchBox.y < face.y + face.height &&
    clenchBox.y + clenchBox.height > face.y;
};

export class HandsFreeTranscriber {
  private endpoint: string;
  private silenceMs: number;
  private minRecordingMs: number;
  private sampleIntervalMs: number;
  private initialThreshold: number;
  private extraFormFields?: Record<string, string>;
  private debugVad: boolean;
  private handsFree: boolean;
  private clenchOnDelayMs: number;
  private clenchOffDelayMs: number;

  // state
  public phase: Phase = "idle";
  private isMicrophone: boolean = false;

  // Microphone control state
  private enabled: boolean = false;
  private detectionMode: MicrophoneControlOptions[] = ["handsFree"];

  // Continuous VAD listening state (for "handsFree" mode)
  private vadListeningStream: MediaStream | null = null;
  private vadListeningCtx: AudioContext | null = null;
  private vadListeningAnalyser: AnalyserNode | null = null;
  private vadListeningSource: MediaStreamAudioSourceNode | null = null;
  private vadListeningTimer: number | null = null;
  private vadListeningBuffer: Float32Array<ArrayBuffer> | null = null;
  private vadListeningThreshold: number = 0;
  private vadListeningCalibrated: boolean = false;
  private isVoiceActive: boolean = false;
  private voiceStartedAt: number = 0;
  private lastVoiceEndAt: number = 0;

  // Debounce state for sticky clench detection
  private clenchIntersectingSince: number | null = null; // timestamp when intersection started
  private clenchNotIntersectingSince: number | null = null; // timestamp when non-intersection started

  // Pre-recording state: start capturing audio immediately on intersection detection,
  // before debounce completes, so we don't miss the first words
  private preRecordingStream: MediaStream | null = null;
  private preRecordingRecorder: MediaRecorder | null = null;
  private preRecordingChunks: BlobPart[] = [];
  private preRecordingStartTime: number = 0;

  public error: string = "";
  public transcriptText: string = "";

  // recording internals
  private stream: MediaStream | null = null;
  private recorder: MediaRecorder | null = null;
  private chunks: BlobPart[] = [];

  // webaudio vad internals
  private audioCtx: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private vadTimer: number | null = null;
  private startedAt = 0;
  private lastVoiceAt = 0;
  private threshold = 0;

  // render internals - self-managed canvas in controller-overlay
  private overlayCanvas: HTMLCanvasElement | null = null;
  private overlayCtx: CanvasRenderingContext2D | null = null;
  private renderTimer: number | null = null;
  private debugMicrophone: boolean = false;

  // Separate state tracking for concurrent recording and transcription
  private isRecording: boolean = false;
  private isTranscribing: boolean = false;
  private lastFaceHitBox: Rect | null = null;

  // prevent concurrent starts
  private busy = false;

  constructor(opts: HandsFreeTranscriberOptions = {}) {
    this.endpoint = opts.endpoint ?? "/api/ai/transcribe";
    this.silenceMs = opts.silenceMs ?? 1200;
    this.minRecordingMs = opts.minRecordingMs ?? 800;
    this.sampleIntervalMs = opts.sampleIntervalMs ?? 50;
    this.initialThreshold = opts.initialThreshold ?? 0; // 0 => auto
    this.extraFormFields = opts.extraFormFields;
    this.debugVad = opts.debugVad ?? false;
    this.handsFree = opts.handsFree ?? true;
    this.clenchOnDelayMs = opts.clenchOnDelayMs ?? 150;
    this.clenchOffDelayMs = opts.clenchOffDelayMs ?? 600;
  }

  /**
   * Apply options from the global options object.
   * This is the main entry point for external configuration.
   * Manages the render loop and VAD listening based on enabled state.
   *
   * When disabling transcription:
   * - Only prevents NEW recordings from starting
   * - Does NOT interfere with recordings currently in progress
   * - Current recordings continue naturally (silence detection, etc.)
   * - Transcriptions complete and send results
   */
  public applyOptions(options: Partial<ConnectOptions> = {}) {
    const wasHandsFree = this.isHandsFreeMode();

    this.detectionMode = options.microphoneDetection || [];
    this.enabled = this.detectionMode?.includes("handsFree");
    this.debugMicrophone = options.debugMicrophone ?? false;

    if (this.debugMicrophone) {
      console.log("Transcriber: applyOptions", {
        enabled: this.enabled,
        detectionMode: this.detectionMode,
        debugMicrophone: this.debugMicrophone,
        isRecording: this.isRecording,
        isTranscribing: this.isTranscribing,
      });
    }

    if (this.enabled) {
      // Start render loop if not running
      this.startRenderLoop();

      // Start VAD listening if in handsFree mode
      if (this.isHandsFreeMode()) {
        this.startVadListening();
      } else if (wasHandsFree && !this.isHandsFreeMode()) {
        // Switching away from handsFree mode
        this.stopVadListening();
      }
    } else {
      // Disable: only prevent NEW recordings, don't interfere with current work

      // Cancel pre-recording (hasn't committed to a full recording yet)
      this.cancelPreRecording();

      // Check if any recording/transcription work is in progress
      // - isRecording/isTranscribing: hands-free mode
      // - busy with recording/uploading/transcribing phase: fist-in-front-of-face mode
      const hasFistModeWork = this.busy &&
        (this.phase === "recording" || this.phase === "uploading" ||
          this.phase === "transcribing");
      const hasActiveWork = this.isRecording || this.isTranscribing ||
        hasFistModeWork;

      if (hasActiveWork) {
        if (this.debugMicrophone) {
          console.log(
            "Transcriber: Recording/transcription in progress, letting it finish naturally",
            {
              isRecording: this.isRecording,
              isTranscribing: this.isTranscribing,
              busy: this.busy,
              phase: this.phase,
            },
          );
        }
        // Keep VAD listening running so silence detection works for current recording (hands-free mode)
        // Keep render loop running to show status
        // Don't call stop() - let the recording finish naturally
        // For fist mode, updateVirtualMicrophoneInputs() will continue tracking to detect fist release
      } else {
        // No work in progress, clean up immediately
        this.stopVadListening();
        this.stopRenderLoop();
        this.setPhase("idle");
      }
    }
  }

  /**
   * Check if we're in hands-free VAD mode
   */
  private isHandsFreeMode(): boolean {
    return this.detectionMode.includes("handsFree");
  }

  /**
   * Check if we're in fist-in-front-of-face mode
   */
  private isFistInFrontOfFaceMode(): boolean {
    return this.detectionMode.includes("fistInFrontOfFace");
  }

  // =====================
  // Self-managed rendering
  // =====================

  /**
   * Create the overlay canvas and insert it into the controller-overlay div.
   */
  private createOverlayCanvas() {
    if (this.overlayCanvas) return;

    const overlay = document.getElementById("controller-overlay");
    if (!overlay) {
      console.warn("Transcriber: controller-overlay element not found");
      return;
    }

    this.overlayCanvas = document.createElement("canvas");
    this.overlayCanvas.id = "transcriber-status-canvas";
    this.overlayCanvas.style.position = "absolute";
    this.overlayCanvas.style.top = "8px";
    this.overlayCanvas.style.left = "8px";
    this.overlayCanvas.style.width = "20px";
    this.overlayCanvas.style.height = "20px";
    this.overlayCanvas.style.pointerEvents = "none";
    this.overlayCanvas.style.zIndex = "1000";
    this.overlayCanvas.width = 20;
    this.overlayCanvas.height = 20;

    this.overlayCtx = this.overlayCanvas.getContext("2d");
    overlay.appendChild(this.overlayCanvas);

    if (this.debugMicrophone) {
      console.log("Transcriber: created overlay canvas");
    }
  }

  /**
   * Remove the overlay canvas from the DOM.
   */
  private destroyOverlayCanvas() {
    if (this.overlayCanvas) {
      this.overlayCanvas.remove();
      this.overlayCanvas = null;
      this.overlayCtx = null;

      if (this.debugMicrophone) {
        console.log("Transcriber: destroyed overlay canvas");
      }
    }
  }

  /**
   * Start the render loop.
   */
  private startRenderLoop() {
    if (this.renderTimer !== null) return; // Already running

    this.createOverlayCanvas();

    // Render at ~30fps
    this.renderTimer = window.setInterval(() => {
      this.renderStatus();
    }, 33);

    if (this.debugMicrophone) {
      console.log("Transcriber: started render loop");
    }
  }

  /**
   * Stop the render loop and cleanup.
   */
  private stopRenderLoop() {
    if (this.renderTimer !== null) {
      window.clearInterval(this.renderTimer);
      this.renderTimer = null;
    }

    this.destroyOverlayCanvas();

    if (this.debugMicrophone) {
      console.log("Transcriber: stopped render loop");
    }
  }

  /**
   * Render the current status to the overlay canvas.
   * - Red circle: recording
   * - Orange circle: uploading/transcribing
   * - Both: red inner circle with orange outer ring
   * - No circle: idle
   */
  private renderStatus() {
    if (!this.overlayCtx || !this.overlayCanvas) return;

    const ctx = this.overlayCtx;
    const size = this.overlayCanvas.width;
    const centerX = size / 2;
    const centerY = size / 2;
    const outerRadius = size / 2 - 2;
    const innerRadius = outerRadius * 0.55;

    // Clear canvas
    ctx.clearRect(0, 0, size, size);

    // Set stroke style for black border
    ctx.strokeStyle = "black";
    ctx.lineWidth = 1;

    // Draw based on current states
    if (this.isRecording && this.isTranscribing) {
      // Both: orange outer ring, red inner circle
      ctx.fillStyle = "rgb(255, 165, 0)";
      ctx.beginPath();
      ctx.arc(centerX, centerY, outerRadius, 0, 2 * Math.PI);
      ctx.fill();
      ctx.stroke();

      ctx.fillStyle = "rgb(255, 0, 0)";
      ctx.beginPath();
      ctx.arc(centerX, centerY, innerRadius, 0, 2 * Math.PI);
      ctx.fill();
      ctx.stroke();
    } else if (this.isRecording) {
      // Red circle for recording only
      ctx.fillStyle = "rgb(255, 0, 0)";
      ctx.beginPath();
      ctx.arc(centerX, centerY, outerRadius, 0, 2 * Math.PI);
      ctx.fill();
      ctx.stroke();
    } else if (this.isTranscribing) {
      // Orange circle for transcribing only
      ctx.fillStyle = "rgb(255, 165, 0)";
      ctx.beginPath();
      ctx.arc(centerX, centerY, outerRadius, 0, 2 * Math.PI);
      ctx.fill();
      ctx.stroke();
    }
    // idle: nothing drawn (canvas is clear)
  }

  /**
   * Start continuous VAD listening (for hands-free mode).
   * Monitors audio levels and triggers recording when voice is detected.
   */
  private async startVadListening() {
    // Already listening
    if (this.vadListeningTimer !== null) return;

    try {
      this.vadListeningStream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
      });

      const AudioContextCtor = window.AudioContext ||
        (window as any).webkitAudioContext;
      this.vadListeningCtx = new AudioContextCtor();
      this.vadListeningSource = this.vadListeningCtx.createMediaStreamSource(
        this.vadListeningStream,
      );

      this.vadListeningAnalyser = this.vadListeningCtx.createAnalyser();
      this.vadListeningAnalyser.fftSize = 2048;
      this.vadListeningSource.connect(this.vadListeningAnalyser);

      this.vadListeningBuffer = new Float32Array(
        this.vadListeningAnalyser.fftSize,
      );

      // Auto-calibrate threshold if not already done
      if (!this.vadListeningCalibrated) {
        const samples: number[] = [];
        const t0 = performance.now();
        while (performance.now() - t0 < 300) {
          this.vadListeningAnalyser.getFloatTimeDomainData(
            this.vadListeningBuffer,
          );
          let sumSq = 0;
          for (let i = 0; i < this.vadListeningBuffer.length; i++) {
            sumSq += this.vadListeningBuffer[i] * this.vadListeningBuffer[i];
          }
          const rms = Math.sqrt(sumSq / this.vadListeningBuffer.length);
          samples.push(rms);
          await sleep(25);
        }
        samples.sort((a, b) => a - b);
        const p75 = samples[Math.floor(samples.length * 0.75)] ?? 0.002;
        this.vadListeningThreshold = clamp(p75 * 2.5 + 0.003, 0.003, 0.06);
        this.vadListeningCalibrated = true;
        if (this.debugVad) {
          console.log(
            "VAD listening threshold:",
            this.vadListeningThreshold,
            "p75:",
            p75,
          );
        }
      }

      // Start polling for voice activity
      this.vadListeningTimer = window.setInterval(() => {
        this.pollVadListening();
      }, this.sampleIntervalMs);
    } catch (e) {
      console.error("Failed to start VAD listening:", e);
      this.stopVadListening();
    }
  }

  /**
   * Poll for voice activity in hands-free mode.
   * When voice is detected, starts recording. When silence is detected, stops and transcribes.
   * Only starts NEW recordings if enabled. Always allows current recordings to finish.
   */
  private pollVadListening() {
    if (!this.vadListeningAnalyser || !this.vadListeningBuffer) return;

    this.vadListeningAnalyser.getFloatTimeDomainData(this.vadListeningBuffer);
    let sumSq = 0;
    for (let i = 0; i < this.vadListeningBuffer.length; i++) {
      sumSq += this.vadListeningBuffer[i] * this.vadListeningBuffer[i];
    }
    const rms = Math.sqrt(sumSq / this.vadListeningBuffer.length);

    const now = performance.now();
    const hasVoice = rms > this.vadListeningThreshold;

    if (hasVoice) {
      if (!this.isVoiceActive) {
        // Voice just started
        this.isVoiceActive = true;
        this.voiceStartedAt = now;
        if (this.debugMicrophone) {
          console.log("Transcriber: Voice started", {
            rms: rms.toFixed(4),
            threshold: this.vadListeningThreshold.toFixed(4),
          });
        }
        // Start recording if not already recording AND if enabled
        // Only prevents NEW recordings, doesn't affect current ones
        if (!this.isRecording && this.enabled) {
          this.recordHandsFree();
        }
      }
      this.lastVoiceEndAt = now;
    } else {
      if (this.isVoiceActive) {
        const silentFor = now - this.lastVoiceEndAt;
        const elapsed = now - this.voiceStartedAt;

        // Check if enough silence to consider voice ended
        if (elapsed >= this.minRecordingMs && silentFor >= this.silenceMs) {
          this.isVoiceActive = false;
          // Stop recording if we're recording (always allow this, even if disabled)
          if (this.isRecording) {
            this.stop();
          }

          // If we were disabled while recording, clean up VAD listening now
          // (the transcription will complete via recordHandsFree/transcribeAsync)
          if (!this.enabled) {
            this.stopVadListening();
          }
        }
      }
    }
  }

  /**
   * Stop continuous VAD listening
   */
  private stopVadListening() {
    if (this.vadListeningTimer !== null) {
      window.clearInterval(this.vadListeningTimer);
      this.vadListeningTimer = null;
    }

    try {
      this.vadListeningSource?.disconnect();
      this.vadListeningAnalyser?.disconnect();
    } catch {}

    this.vadListeningSource = null;
    this.vadListeningAnalyser = null;
    this.vadListeningBuffer = null;

    const ctx = this.vadListeningCtx;
    this.vadListeningCtx = null;
    if (ctx && ctx.state !== "closed") ctx.close().catch(() => {});

    if (this.vadListeningStream) {
      this.vadListeningStream.getTracks().forEach((t) => t.stop());
      this.vadListeningStream = null;
    }

    this.isVoiceActive = false;
  }

  /**
   * Start recording in hands-free mode (triggered by VAD).
   * Recording continues until silence is detected in the VAD polling loop.
   * Allows starting a new recording while a previous transcription is still in progress.
   * Always completes the recording and sends for transcription, even if disabled mid-flight.
   */
  private async recordHandsFree(): Promise<void> {
    // Only block if we're already recording (not if just transcribing)
    if (this.isRecording) {
      return;
    }

    // Don't start NEW recordings if disabled
    if (!this.enabled) {
      if (this.debugMicrophone) {
        console.log("Transcriber: Not starting new recording (disabled)");
      }
      return;
    }

    if (this.debugMicrophone) {
      console.log("Transcriber: Starting recording (hands-free mode)");
    }

    this.isRecording = true;
    this.error = "";
    this.setPhase("recording");

    try {
      const file = await this.recordUntilStopped();
      this.isRecording = false;
      // Start transcription asynchronously so we can record again while it processes
      // This will complete even if transcription was disabled mid-flight
      this.transcribeAsync(file);

      // Update phase based on current state
      this.setPhase(this.isTranscribing ? "transcribing" : "idle");

      // If disabled and no transcription pending, clean up now
      if (!this.enabled && !this.isTranscribing) {
        this.stopRenderLoop();
      }
    } catch (e: any) {
      this.isRecording = false;
      const errorMsg = e?.message ?? String(e);
      if (
        errorMsg.includes("No valid audio chunks") ||
        errorMsg.includes("Audio blob is empty")
      ) {
        if (this.debugMicrophone) {
          console.warn("Transcriber: Recording produced no audio:", errorMsg);
        }
        this.setPhase(this.isTranscribing ? "transcribing" : "idle");
        // If disabled and no transcription pending, clean up now
        if (!this.enabled && !this.isTranscribing) {
          this.stopRenderLoop();
        }
        return;
      }
      this.error = errorMsg;
      if (this.debugMicrophone) {
        console.error("Transcriber: Error during recording:", errorMsg);
      }
      this.setPhase("error");
      // If disabled and no transcription pending, clean up now
      if (!this.enabled && !this.isTranscribing) {
        this.stopRenderLoop();
      }
    }
  }

  /**
   * Upload and transcribe a file asynchronously.
   * This allows new recordings to start while transcription is in progress.
   * Always completes even if transcription was disabled mid-flight.
   */
  private async transcribeAsync(file: File): Promise<void> {
    this.isTranscribing = true;
    this.setPhase(this.isRecording ? "recording" : "uploading");

    try {
      const result = await this.uploadForTranscription(file);

      this.transcriptText = result;
      if (result) {
        // Always send the result, even if transcription was disabled mid-flight
        const outputData = {
          transcription: {
            time: Date.now(),
            text: result,
          },
        };
        globalThis.metaframe?.setOutputs(outputData);
      }
    } catch (e: any) {
      const errorMsg = e?.message ?? String(e);
      console.error("Transcriber: Error during transcription:", errorMsg, e);
      this.error = errorMsg;
    } finally {
      this.isTranscribing = false;
      // Update phase based on current state
      if (!this.isRecording) {
        this.setPhase("idle");
        // If transcription was disabled while we were working, clean up the render loop now
        if (!this.enabled) {
          this.stopRenderLoop();
        }
      }
    }
  }

  /**
   * If there is one fist/clench intersecting the face, it's a microphone.
   * Only active when in "fistInFrontOfFace" detection mode.
   *
   * Important: If a fist-mode recording is in progress (busy && phase === "recording"),
   * we continue tracking even if the mode changed. This ensures recordings finish
   * when the user releases their fist, even if transcription was disabled mid-recording.
   */
  public updateVirtualMicrophoneInputs(
    clenchData: ClenchData | undefined,
    pose: MediapipePoseResults | undefined,
  ) {
    // Update face hitbox for debug rendering regardless of mode
    const faceHitBox: Rect | null = pose ? getFaceHitBoxFromPose(pose) : null;
    if (faceHitBox) {
      this.lastFaceHitBox = faceHitBox;
    }

    // Check if we have an in-progress fist-mode recording that needs to finish
    const hasFistModeRecordingInProgress = this.busy &&
      this.phase === "recording";

    // Skip clench detection if not in fistInFrontOfFace mode AND no recording in progress
    // We must continue tracking for in-progress recordings so they can finish when fist is released
    if (!this.isFistInFrontOfFaceMode() && !hasFistModeRecordingInProgress) {
      return;
    }

    const [leftHandBox, rightHandBox] = clenchData?.handHitBoxes ||
      EMPTY_ARRAY;
    const { isClenchLeft, isClenchRight } = clenchData || EMPTY_OBJECT;
    const isBothClenched = isClenchLeft && isClenchRight;
    const isNoneClenched = !isClenchLeft && !isClenchRight;
    const clenchBox = !isBothClenched && !isNoneClenched
      ? leftHandBox || rightHandBox
      : undefined;

    const isCurrentlyIntersecting = isClenchBoxIntersectingFace(
      clenchBox,
      faceHitBox,
    );
    const now = performance.now();

    // Update debounce tracking timestamps
    if (isCurrentlyIntersecting) {
      // Reset "not intersecting" timer when we detect intersection
      this.clenchNotIntersectingSince = null;
      // Start "intersecting" timer if not already started
      if (this.clenchIntersectingSince === null) {
        this.clenchIntersectingSince = now;
        // Start pre-recording immediately when intersection is first detected
        // This captures audio before debounce completes, so we don't miss first words
        if (this.phase === "idle") {
          this.startPreRecording();
        }
      }
    } else {
      // Reset "intersecting" timer when we detect no intersection
      this.clenchIntersectingSince = null;
      // Start "not intersecting" timer if not already started
      if (this.clenchNotIntersectingSince === null) {
        this.clenchNotIntersectingSince = now;
      }
      // Cancel pre-recording if debounce failed (gesture wasn't sustained)
      if (this.preRecordingRecorder && !this.isMicrophone) {
        this.cancelPreRecording();
      }
    }

    // Determine "sticky" microphone state with hysteresis
    // Only turn ON after consistent intersection for clenchOnDelayMs
    const shouldTurnOn = isCurrentlyIntersecting &&
      this.clenchIntersectingSince !== null &&
      (now - this.clenchIntersectingSince) >= this.clenchOnDelayMs;

    // Only turn OFF after consistent non-intersection for clenchOffDelayMs
    const shouldTurnOff = !isCurrentlyIntersecting &&
      this.clenchNotIntersectingSince !== null &&
      (now - this.clenchNotIntersectingSince) >= this.clenchOffDelayMs;

    // Update isMicrophone state with sticky behavior
    if (shouldTurnOn) {
      this.isMicrophone = true;
    } else if (shouldTurnOff) {
      this.isMicrophone = false;
    }
    // Otherwise, keep the previous isMicrophone state (sticky)

    switch (this.phase) {
      case "idle":
        // Only start NEW recordings if in fist mode AND fist is detected
        if (!this.isMicrophone || !this.isFistInFrontOfFaceMode()) {
          return;
        }
        this.record();
        break;
      case "recording":
        // Always allow stopping in-progress recordings when fist is released
        // This works even if the mode was changed mid-recording
        if (!this.isMicrophone) {
          this.stop();
        }
        break;
      case "uploading":
        break;
      case "transcribing":
        break;
      case "done":
      case "error":
        // Reset to idle when user releases the microphone gesture
        // This allows them to start a new recording (only if still in fist mode)
        if (!this.isMicrophone && this.isFistInFrontOfFaceMode()) {
          this.setPhase("idle");
        }
        break;
      default:
        break;
    }
    // if (inputs.startListening) this.start().catch(() => {});
    // if (inputs.stopListening) this.stop();

    // if (typeof inputs.shouldListen === "boolean") {
    //   if (inputs.shouldListen) this.start().catch(() => {});
    //   else this.stop();
    // }
  }

  /**
   * Start hands-free recording (if not already recording).
   * When silence triggers stop, it will automatically upload and update transcriptText.
   * Always completes the recording and transcription, even if disabled mid-flight.
   */
  public async start(): Promise<string> {
    if (this.busy) return this.transcriptText;
    if (
      this.phase === "recording" || this.phase === "uploading" ||
      this.phase === "transcribing"
    ) {
      return this.transcriptText;
    }

    this.busy = true;
    this.error = "";
    this.transcriptText = "";
    this.setPhase("recording");

    try {
      const file = await this.recordUntilSilence(); // resolves when recording stops

      this.setPhase("uploading");
      const result = await this.uploadForTranscription(file);

      this.transcriptText = result;
      this.setPhase("done");
      return result;
    } catch (e: any) {
      const errorMsg = e?.message ?? String(e);
      // Don't treat empty/short recordings as errors - just log and return empty
      if (
        errorMsg.includes("No valid audio chunks") ||
        errorMsg.includes("Audio blob is empty")
      ) {
        console.warn("Recording produced no audio:", errorMsg);
        this.setPhase("idle");
        return "";
      }
      this.error = errorMsg;
      this.setPhase("error");
      throw e;
    } finally {
      this.busy = false;
      // If transcription was disabled while we were working, clean up
      // Check both enabled (hands-free) and fist mode
      if (!this.enabled && !this.isFistInFrontOfFaceMode()) {
        this.stopVadListening();
        this.stopRenderLoop();
      }
    }
  }

  /**
   * Start recording and keep recording until stop() is called manually.
   * No silence detection - you must call stop() to end recording and trigger transcription.
   * Returns the transcript text after upload completes.
   * Always completes the recording and transcription, even if disabled mid-flight.
   */
  public async record(): Promise<string> {
    if (this.busy) return this.transcriptText;
    if (
      this.phase === "recording" || this.phase === "uploading" ||
      this.phase === "transcribing"
    ) {
      return this.transcriptText;
    }

    this.busy = true;
    this.error = "";
    this.transcriptText = "";
    this.setPhase("recording");
    const sendRecordingInterval = setInterval(() => {
      globalThis.metaframe?.setOutputs({
        transcription: {
          time: Date.now(),
          recording: true,
        },
      });
    }, 1000);
    setTimeout(() => {
      clearInterval(sendRecordingInterval);
    }, 10000);

    try {
      console.log("Transcriber: record() awaiting recordUntilStopped...");
      const file = await this.recordUntilStopped();
      console.log("Transcriber: record() - recording complete", {
        fileSize: file.size,
        fileType: file.type,
        enabled: this.enabled,
        isFistMode: this.isFistInFrontOfFaceMode(),
      });

      this.setPhase("uploading");
      console.log("Transcriber: record() - calling uploadForTranscription...");
      const result = await this.uploadForTranscription(file);
      console.log("Transcriber: record() - uploadForTranscription returned", {
        resultLength: result?.length ?? 0,
        hasResult: !!result,
      });

      // If upload was skipped due to validation, still mark as done but with empty result
      // Always send the result, even if transcription was disabled mid-flight
      this.transcriptText = result;
      if (result) {
        console.log(
          "Transcriber: record() - calling setOutputs with result:",
          result,
        );
        const outputData = {
          transcription: {
            time: Date.now(),
            text: result,
          },
        };
        globalThis.metaframe?.setOutputs(outputData);
        console.log("Transcriber: record() - setOutputs called successfully");
      } else {
        console.log(
          "Transcriber: record() - no result, NOT calling setOutputs",
        );
      }
      this.setPhase("done");
      clearInterval(sendRecordingInterval);
      return result;
    } catch (e: any) {
      const errorMsg = e?.message ?? String(e);
      console.error("Transcriber: record() - error:", errorMsg, e);
      // Don't treat empty/short recordings as errors - just log and return empty
      if (
        errorMsg.includes("No valid audio chunks") ||
        errorMsg.includes("Audio blob is empty")
      ) {
        console.warn("Transcriber: record() - no audio produced:", errorMsg);
        this.setPhase("idle");
        clearInterval(sendRecordingInterval);
        return "";
      }
      this.error = errorMsg;
      this.setPhase("error");
      clearInterval(sendRecordingInterval);
      throw e;
    } finally {
      console.log("Transcriber: record() - finally block", {
        enabled: this.enabled,
        isFistMode: this.isFistInFrontOfFaceMode(),
      });
      this.busy = false;
      clearInterval(sendRecordingInterval);
      // If transcription was disabled while we were working, clean up
      // Check both enabled (hands-free) and fist mode
      if (!this.enabled && !this.isFistInFrontOfFaceMode()) {
        console.log("Transcriber: record() - cleaning up (all modes disabled)");
        this.stopVadListening();
        this.stopRenderLoop();
      }
    }
  }

  /**
   * Stop recording immediately (if recording).
   * Note: if you stop manually, it will still proceed to upload/transcribe.
   */
  public stop() {
    console.log("Transcriber: stop() called", {
      hasVadTimer: this.vadTimer != null,
      hasRecorder: !!this.recorder,
      recorderState: this.recorder?.state,
      phase: this.phase,
      isRecording: this.isRecording,
      busy: this.busy,
    });

    // Stop VAD timer immediately
    if (this.vadTimer != null) {
      window.clearInterval(this.vadTimer);
      this.vadTimer = null;
    }

    const r = this.recorder;
    if (r && r.state !== "inactive") {
      console.log("Transcriber: Calling recorder.stop()");
      r.stop();
    } else {
      console.log("Transcriber: Recorder not active, nothing to stop");
    }
  }

  /**
   * Start pre-recording immediately when intersection is first detected.
   * This captures audio before the debounce completes, so we don't miss first words.
   */
  private async startPreRecording() {
    if (this.preRecordingRecorder) return; // Already pre-recording

    try {
      this.preRecordingStream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
      });

      const mimeType = pickMimeType();
      this.preRecordingRecorder = new MediaRecorder(
        this.preRecordingStream,
        mimeType ? { mimeType } : undefined,
      );
      this.preRecordingChunks = [];
      this.preRecordingStartTime = performance.now();

      this.preRecordingRecorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) {
          this.preRecordingChunks.push(e.data);
        }
      };

      // Start with timeslice to get chunks periodically (every 100ms)
      this.preRecordingRecorder.start(100);
    } catch (e) {
      console.error("Failed to start pre-recording:", e);
      this.cancelPreRecording();
    }
  }

  /**
   * Cancel pre-recording and clean up resources.
   * Called when the debounce fails (gesture wasn't sustained).
   */
  private cancelPreRecording() {
    if (
      this.preRecordingRecorder &&
      this.preRecordingRecorder.state !== "inactive"
    ) {
      this.preRecordingRecorder.stop();
    }
    if (this.preRecordingStream) {
      this.preRecordingStream.getTracks().forEach((t) => t.stop());
    }
    this.preRecordingRecorder = null;
    this.preRecordingStream = null;
    this.preRecordingChunks = [];
    this.preRecordingStartTime = 0;
  }

  /**
   * Finalize pre-recording and transfer to main recording.
   * Returns the stream and chunks that were pre-recorded.
   */
  private finalizePreRecording(): {
    stream: MediaStream;
    chunks: BlobPart[];
  } | null {
    if (!this.preRecordingRecorder || !this.preRecordingStream) {
      return null;
    }

    // Stop the pre-recording recorder to finalize any pending data
    if (this.preRecordingRecorder.state !== "inactive") {
      this.preRecordingRecorder.stop();
    }

    const result = {
      stream: this.preRecordingStream,
      chunks: [...this.preRecordingChunks],
    };

    // Clear pre-recording state but don't stop the stream (it will be reused)
    this.preRecordingRecorder = null;
    this.preRecordingStream = null;
    this.preRecordingChunks = [];
    this.preRecordingStartTime = 0;

    return result;
  }

  // -----------------------------
  // Internals
  // -----------------------------

  private async recordUntilSilence(): Promise<File> {
    if (!navigator.mediaDevices?.getUserMedia) {
      throw new Error("getUserMedia is not available");
    }
    if (typeof MediaRecorder === "undefined") {
      throw new Error("MediaRecorder is not available");
    }

    this.chunks = [];

    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true },
    });

    // Setup recorder
    const mimeType = pickMimeType();
    this.recorder = new MediaRecorder(
      this.stream,
      mimeType ? { mimeType } : undefined,
    );

    const done = new Promise<File>((resolve, reject) => {
      if (!this.recorder) return reject(new Error("Recorder init failed"));

      this.recorder.onerror = (err) =>
        reject(err ?? new Error("Recorder error"));

      this.recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) this.chunks.push(e.data);
      };

      this.recorder.onstop = () => {
        try {
          // Filter out empty chunks
          const validChunks = this.chunks.filter((chunk) => {
            if (chunk instanceof Blob) return chunk.size > 0;
            return true;
          });

          if (validChunks.length === 0) {
            this.cleanupAudio();
            reject(new Error("No valid audio chunks recorded"));
            return;
          }

          const blob = new Blob(validChunks, {
            type: this.recorder?.mimeType || "audio/webm",
          });

          if (blob.size === 0) {
            this.cleanupAudio();
            reject(new Error("Audio blob is empty"));
            return;
          }

          const ext = blob.type.includes("ogg") ? "ogg" : "webm";
          const file = new File([blob], `recording.${ext}`, {
            type: blob.type,
          });

          this.cleanupAudio();
          resolve(file);
        } catch (e) {
          this.cleanupAudio();
          reject(e);
        }
      };
    });

    // Setup WebAudio VAD
    const AudioContextCtor = window.AudioContext ||
      (window as any).webkitAudioContext;
    this.audioCtx = new AudioContextCtor();
    this.source = this.audioCtx.createMediaStreamSource(this.stream);

    this.analyser = this.audioCtx.createAnalyser();
    this.analyser.fftSize = 2048;
    this.source.connect(this.analyser);

    const buf = new Float32Array(this.analyser.fftSize);

    // Init timers/threshold
    this.startedAt = performance.now();
    this.lastVoiceAt = this.startedAt;
    this.threshold = this.initialThreshold;

    // Auto-calibrate threshold against ambient RMS
    if (this.threshold <= 0) {
      const samples: number[] = [];
      const t0 = performance.now();
      while (performance.now() - t0 < 300) {
        this.analyser.getFloatTimeDomainData(buf);
        let sumSq = 0;
        for (let i = 0; i < buf.length; i++) sumSq += buf[i] * buf[i];
        const rms = Math.sqrt(sumSq / buf.length);
        samples.push(rms);
        await sleep(25);
      }
      samples.sort((a, b) => a - b);
      const p75 = samples[Math.floor(samples.length * 0.75)] ?? 0.002;
      this.threshold = clamp(p75 * 2.5 + 0.003, 0.003, 0.06);
      if (this.debugVad) {
        console.log("Auto threshold:", this.threshold, "p75:", p75);
      }
    }

    // Start recorder
    this.recorder.start();
    // this.requestRender();

    // Poll RMS; stop after silenceMs
    this.vadTimer = window.setInterval(() => {
      if (!this.analyser || !this.recorder) return;

      this.analyser.getFloatTimeDomainData(buf);
      let sumSq = 0;
      for (let i = 0; i < buf.length; i++) sumSq += buf[i] * buf[i];
      const rms = Math.sqrt(sumSq / buf.length);

      const now = performance.now();
      const hasVoice = rms > this.threshold;

      if (hasVoice) this.lastVoiceAt = now;

      const elapsed = now - this.startedAt;
      const silentFor = now - this.lastVoiceAt;

      // Only auto-stop on silence when handsFree mode is enabled
      if (
        this.handsFree && elapsed >= this.minRecordingMs &&
        silentFor >= this.silenceMs
      ) {
        if (this.debugVad) {
          console.log("Silence stop:", {
            rms,
            threshold: this.threshold,
            silentFor,
            elapsed,
          });
        }
        this.stop();
      }
    }, this.sampleIntervalMs);

    return done;
  }

  /**
   * Records audio until stop() is called manually. No silence detection.
   * If pre-recording is active, uses the already-captured audio to avoid missing first words.
   */
  private async recordUntilStopped(): Promise<File> {
    if (!navigator.mediaDevices?.getUserMedia) {
      throw new Error("getUserMedia is not available");
    }
    if (typeof MediaRecorder === "undefined") {
      throw new Error("MediaRecorder is not available");
    }

    // Check if we have pre-recorded audio to use
    const preRecorded = this.finalizePreRecording();

    if (preRecorded) {
      // Use the pre-recorded stream and chunks
      this.chunks = [...preRecorded.chunks]; // Include pre-recorded chunks
      this.stream = preRecorded.stream;
    } else {
      // No pre-recording, acquire stream fresh (may miss first words)
      this.chunks = [];
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
      });
    }

    // Setup recorder
    const mimeType = pickMimeType();
    this.recorder = new MediaRecorder(
      this.stream,
      mimeType ? { mimeType } : undefined,
    );

    const done = new Promise<File>((resolve, reject) => {
      if (!this.recorder) return reject(new Error("Recorder init failed"));

      this.recorder.onerror = (err) =>
        reject(err ?? new Error("Recorder error"));

      this.recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) this.chunks.push(e.data);
      };

      this.recorder.onstop = () => {
        try {
          // Filter out empty chunks
          const validChunks = this.chunks.filter((chunk) => {
            if (chunk instanceof Blob) return chunk.size > 0;
            return true;
          });

          if (validChunks.length === 0) {
            this.cleanupAudio();
            reject(new Error("No valid audio chunks recorded"));
            return;
          }

          const blob = new Blob(validChunks, {
            type: this.recorder?.mimeType || "audio/webm",
          });

          if (blob.size === 0) {
            this.cleanupAudio();
            reject(new Error("Audio blob is empty"));
            return;
          }

          const ext = blob.type.includes("ogg") ? "ogg" : "webm";
          const file = new File([blob], `recording.${ext}`, {
            type: blob.type,
          });

          this.cleanupAudio();
          resolve(file);
        } catch (e) {
          this.cleanupAudio();
          reject(e);
        }
      };
    });

    // Start recorder - no VAD setup, just record until stop() is called
    this.recorder.start();

    return done;
  }

  private async uploadForTranscription(file: File): Promise<string> {
    console.log("Transcriber: uploadForTranscription called", {
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      endpoint: this.endpoint,
    });

    // Basic validation to avoid uploading bad audio data
    const MIN_FILE_SIZE = 1000; // 1KB minimum - anything smaller is likely corrupt/empty
    const MIN_DURATION_MS = 100; // Minimum recording duration we'd expect

    if (!file || file.size === 0) {
      console.warn("Transcriber: Skipping transcription: empty audio file");
      return "";
    }

    if (file.size < MIN_FILE_SIZE) {
      console.warn(
        `Transcriber: Skipping transcription: audio file too small (${file.size} bytes)`,
      );
      return "";
    }

    // Check if it's a valid audio MIME type
    const validMimeTypes = [
      "audio/webm",
      "audio/ogg",
      "audio/mp4",
      "audio/mpeg",
      "audio/wav",
    ];
    const isValidMime = validMimeTypes.some((mime) =>
      file.type.startsWith(mime.split("/")[0])
    );
    if (!isValidMime && file.type) {
      console.warn(
        `Transcriber: Skipping transcription: unexpected file type "${file.type}"`,
      );
      return "";
    }

    const fd = new FormData();
    fd.append("file", file);

    if (this.extraFormFields) {
      for (const [k, v] of Object.entries(this.extraFormFields)) {
        fd.append(k, v);
      }
    }

    this.setPhase("transcribing");
    // this.requestRender();

    console.log("Transcriber: Sending fetch request to", this.endpoint);
    const resp = await fetch(this.endpoint, { method: "POST", body: fd });
    const respText = await resp.text();
    console.log("Transcriber: Fetch response", {
      status: resp.status,
      ok: resp.ok,
      respTextLength: respText.length,
      respTextPreview: respText.substring(0, 200),
    });

    if (!resp.ok) throw new Error(`${resp.status}: ${respText}`);

    // Expect JSON { text: "..." } but tolerate plain text
    try {
      const data = JSON.parse(respText) as { text?: string };
      console.log("Transcriber: Parsed JSON response", { text: data.text });
      return data.text ?? "";
    } catch {
      console.log("Transcriber: Response is plain text, returning as-is");
      return respText;
    }
  }

  private cleanupAudio() {
    if (this.vadTimer != null) {
      window.clearInterval(this.vadTimer);
      this.vadTimer = null;
    }

    try {
      this.source?.disconnect();
      this.analyser?.disconnect();
    } catch {}

    this.source = null;
    this.analyser = null;

    const ctx = this.audioCtx;
    this.audioCtx = null;
    if (ctx && ctx.state !== "closed") ctx.close().catch(() => {});

    if (this.stream) {
      this.stream.getTracks().forEach((t) => t.stop());
      this.stream = null;
    }

    this.recorder = null;
    this.chunks = [];

    // Also clean up any pre-recording state
    this.cancelPreRecording();
  }

  private setPhase(p: Phase) {
    this.phase = p;
    // this.requestRender();
  }

  // private requestRender() {
  //   // no scheduler assumptions; just re-render if we have a canvas
  //   if (this.lastCanvas) this.render(this.lastCanvas);
  // }

  private wrapText(
    text: string,
    maxWidth: number,
    ctx: CanvasRenderingContext2D,
  ): string[] {
    const words = text.split(/\s+/);
    const lines: string[] = [];
    let line = "";

    for (const word of words) {
      const test = line ? `${line} ${word}` : word;
      const w = ctx.measureText(test).width;
      if (w <= maxWidth) {
        line = test;
      } else {
        if (line) lines.push(line);
        line = word;
      }
    }
    if (line) lines.push(line);
    return lines;
  }
}
